import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Button, LinearProgress, Modal, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { Company, CompanyUser } from "../../models";
import { createCompany, getCompanyUsers, getUsers, setCompanyUsers, updateCompany } from "../../api";

interface CompanyFormDialogProps {
  // Undefined when creating a new company
  company?: Company;
  onClose: () => void;
  onCompanySave: () => void;
}

export const CompanyFormDialog: React.FC<CompanyFormDialogProps> = ({ company, onClose, onCompanySave }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState(company?.name ?? "");
  const [users, setUsers] = useState<CompanyUser[] | null>(null);
  const [members, setMembers] = useState<CompanyUser[] | null>(company ? null : []);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const loading = useMemo(() => (!users || !members) && !error, [users, members, error]);
  const nameError = name.trim().length === 0 ? "Name is required" : undefined;

  useEffect(() => {
    async function fetchData() {
      try {
        setUsers(await getUsers());
        if (company) {
          setMembers(await getCompanyUsers(company.id));
        }
      } catch (e) {
        setError("Failed to load users");
        console.error(e);
      }
    }

    fetchData();
  }, [company]);

  async function handleSave() {
    if (submitting || nameError) {
      return;
    }

    setSubmitting(true);
    enqueueSnackbar("Saving...", { variant: "info" });

    try {
      const saved = company
        ? await updateCompany(company.id, { name: name.trim() })
        : await createCompany({ name: name.trim() });

      await setCompanyUsers(saved.id, (members ?? []).map(member => member.id));

      enqueueSnackbar("Company saved", { variant: "success" });
      onCompanySave();
    } catch (e) {
      enqueueSnackbar((e as Error).message || "Failed to save company", { variant: "error" });
      console.error(e);
    }

    setSubmitting(false);
  }

  return (
    <Modal open={true} onClose={onClose}>
      <div className="edit-item-container">
        {loading && <div style={{ width: "50%", margin: "auto" }}>
          <LinearProgress />
        </div>}
        {error && <div>Error: {error}</div>}
        {!loading && !error && <div className="social">
          <div className="box post-edit" style={{ margin: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
            <TextField
              variant="outlined"
              label="Name"
              value={name}
              error={!!nameError}
              helperText={nameError}
              onChange={(e) => setName(e.target.value)}
            />
            <Autocomplete
              multiple
              options={users ?? []}
              value={members ?? []}
              getOptionLabel={(option) => option.username}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => setMembers(value)}
              renderInput={(params) => <TextField {...params} variant="outlined" label="Users" />}
            />
            <Button variant="contained" color="primary" onClick={onClose}>Close</Button>
            <Button variant="contained" color="primary" disabled={submitting || !!nameError} onClick={handleSave}>Save</Button>
          </div>
        </div>}
      </div>
    </Modal>
  );
};
